import type { IntlShape } from '@kyruus/intl';

import type {
  SearchFacetWithAppliedTerms,
  SearchModification,
  Term
} from 'Src/types';
import { messages } from '../../messages';

/** An option shown in the typeahead. */
export interface TypeaheadOption extends Pick<Term, 'count'> {
  value: string;
  label: string;
  /** Group heading the option is displayed under, if any. */
  group?: string;
}

/** A facet term with its translated label resolved. */
export interface LabeledTerm {
  label: string;
  value: string;
  applied: boolean;
  top_filter?: boolean;
  count: number;
}

/**
 * Resolve the placeholder text for a typeahead facet, falling back to the
 * default placeholder when the facet has no configured placeholder message.
 *
 * We want the default text here to be sent to translators, but since the ID
 * can't be determined statically, we can't send it through the defineMessages API.
 */
export const getPlaceholder = (intl: IntlShape, facetField: string): string => {
  const placeholderMessageDescriptor = {
    id: `facet.${facetField}.typeahead.placeholder`,
    defaultMessage: ''
  };
  return intl.messages[placeholderMessageDescriptor.id]
    ? intl.formatMessage(placeholderMessageDescriptor)
    : intl.formatMessage(messages.typeaheadDefaultPlaceholder);
};

/**
 * Map a facet's raw terms into terms with their translated labels resolved.
 */
export const getFacetTerms = (
  intl: IntlShape,
  facet: SearchFacetWithAppliedTerms
): LabeledTerm[] =>
  facet.terms.map((term) => ({
    label: intl.formatMessage({
      id: `field.value.${facet.field}.${term.value}`,
      defaultMessage: term.value,
      description: 'The label for the filter'
    }),
    value: term.filter_param,
    applied: term.applied,
    top_filter: term.top_filter,
    count: term.count
  }));

/**
 * Get the values of the currently applied terms.
 */
export const getAppliedValues = (facetTerms: LabeledTerm[]): string[] =>
  facetTerms.filter((term) => term.applied).map((term) => term.value);

/**
 * Partition terms into the options list shown in the typeahead. When any terms
 * are flagged as top filters, options are split into "Popular choices" and
 * "All choices" groups; otherwise a single flat list is returned. Top filters
 * are pulled out of the "All choices" group so the two groups form a clean
 * partition rather than duplicating the popular options.
 */
export const getGroupedOptions = (
  intl: IntlShape,
  facetTerms: LabeledTerm[]
): { options: TypeaheadOption[]; grouped: boolean } => {
  const topFilterLabel = intl.formatMessage(messages.topfilter);
  const allFilterLabel = intl.formatMessage(messages.allfilter);

  const grouped = facetTerms.some((term) => term.top_filter);
  const topFilters: TypeaheadOption[] = facetTerms
    .filter((term) => term.top_filter)
    .map((term) => ({
      value: term.value,
      label: term.label,
      count: term.count,
      group: topFilterLabel
    }));
  const remainingFilters: TypeaheadOption[] = facetTerms
    .filter((term) => !term.top_filter)
    .map((term) => ({
      value: term.value,
      label: term.label,
      count: term.count,
      group: grouped ? allFilterLabel : undefined
    }));

  return { options: [...topFilters, ...remainingFilters], grouped };
};

/**
 * Diff the selected values against the applied values and build the search
 * modification needed to add or remove the single changed filter.
 */
export const getSearchModifications = (
  selectedValues: string[],
  appliedValues: string[]
): SearchModification[] => {
  if (selectedValues.length > appliedValues.length) {
    const [value] = selectedValues.filter((v) => !appliedValues.includes(v));
    return [{ action: 'append', key: 'filter', value }];
  }
  if (selectedValues.length < appliedValues.length) {
    const [value] = appliedValues.filter((v) => !selectedValues.includes(v));
    return [{ action: 'delete_key_value', key: 'filter', value }];
  }
  return [];
};
